<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCsmlResizable } from '../../composables/useCsmlResizable';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'preview',
    validator: value => ['preview', 'script'].includes(value),
  },
  previewContent: { type: String, default: '' },
  // Why the last Build attempt failed, shown under the editor.
  error: { type: String, default: '' },
});
const emit = defineEmits(['build', 'copy', 'close', 'update:mode']);
const { t } = useI18n();
const scriptText = ref('');
const { width, startResize } = useCsmlResizable('csml-builder-code-width', {
  min: 320,
  max: 720,
  initial: 360,
});

const tabs = computed(() => [
  { label: t('AGENT_BOTS.VISUAL_BUILDER.PREVIEW'), value: 'preview' },
  { label: t('AGENT_BOTS.VISUAL_BUILDER.SCRIPT'), value: 'script' },
]);
const activeIndex = computed(() =>
  tabs.value.findIndex(tab => tab.value === props.mode)
);

watch(
  () => props.previewContent,
  value => {
    if (props.mode === 'script' && !scriptText.value) {
      scriptText.value = value || '';
    }
  },
  { immediate: true }
);

// Entering the Script tab seeds the editor with the current flow, so the user
// edits what is on the canvas rather than an empty box.
watch(
  () => props.mode,
  mode => {
    if (mode === 'script' && !scriptText.value) {
      scriptText.value = props.previewContent || '';
    }
  }
);

const onBuild = () => {
  emit('build', scriptText.value);
};
</script>

<template>
  <aside
    class="relative flex flex-col h-full overflow-hidden border shadow-lg pointer-events-auto rounded-xl border-n-weak bg-n-solid-1/95 backdrop-blur"
    :style="{ width: `${width}px` }"
  >
    <div
      class="absolute inset-y-0 z-10 w-1.5 cursor-col-resize start-0 hover:bg-n-brand/30"
      @mousedown="startResize"
    />
    <header
      class="flex items-center justify-between gap-2 px-3 py-2 border-b border-n-weak shrink-0"
    >
      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeIndex"
        @tab-changed="emit('update:mode', $event.value)"
      />
      <Button
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-x"
        is-icon-only
        @click="emit('close')"
      />
    </header>
    <div
      v-if="mode === 'preview'"
      class="flex flex-col flex-1 min-h-0 gap-2 p-3 overflow-auto"
    >
      <Button
        class="self-end"
        size="xs"
        variant="faded"
        color="slate"
        icon="i-lucide-copy"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.COPY')"
        @click="emit('copy')"
      />
      <pre
        class="p-3 font-mono text-xs break-words whitespace-pre-wrap rounded-lg bg-n-solid-3 text-n-slate-12"
        >{{ previewContent }}</pre
      >
    </div>
    <div v-else class="flex flex-col flex-1 min-h-0 gap-2 p-3">
      <p class="text-xs text-n-slate-11">
        {{ t('AGENT_BOTS.VISUAL_BUILDER.SCRIPT_HINT') }}
      </p>
      <TextArea
        v-model="scriptText"
        class="flex-1 min-h-[200px] font-mono text-xs"
      />
      <p
        v-if="error"
        class="flex items-start gap-1.5 text-xs text-n-ruby-11"
        role="alert"
      >
        <Icon icon="i-lucide-circle-alert" class="mt-px size-3.5 shrink-0" />
        {{ error }}
      </p>
      <Button
        color="blue"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.BUILD')"
        @click="onBuild"
      />
    </div>
  </aside>
</template>
